import { useState, useEffect } from 'react';
import { useQuery } from "@tanstack/react-query";
import TemplateService from '../../../../AppSettings/services/TemplateService';
import TablesService from '../../../../AppSettings/services/TablesService';
import { TABLE_TYPES, MESSAGES } from '../Utils/constants';

/**
 * Hook to manage table data fetching and state
 */
export const useTableData = () => {
  const [customTables, setCustomTables] = useState([]);
  const [allTables, setAllTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedTableData, setSelectedTableData] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  // Fetch template names using React Query
  // Handle errors gracefully - if getChecklistTemplates doesn't exist or fails, return empty array
  const {
    data: templateNames = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["templates"],
    queryFn: async () => {
      try {
        // Check if the method exists before calling it
        if (TemplateService.getChecklistTemplates) {
          return await TemplateService.getChecklistTemplates();
        }
        return [];
      } catch (err) {
        console.warn('Error fetching checklist templates (this is OK if only using custom tables):', err);
        return []; // Return empty array on error instead of throwing
      }
    },
    staleTime: 10 * 60 * 1000, // 10 min before refetching
    cacheTime: 60 * 60 * 1000, // 1 hour cache
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false, // Don't retry on error
  });

  // Fetch custom tables on mount
  useEffect(() => {
    fetchCustomTables();
  }, []);

  const fetchCustomTables = async () => {
    try {
      const response = await TablesService.getCustomTables();
      const custom = Array.isArray(response) ? response : (response?.data ?? []);
      setCustomTables(custom);
      // Only set custom tables in allTables
      const merged = custom.map(t => ({ ...t, _type: TABLE_TYPES.CUSTOM }))
        .sort((a, b) => a.table_name.localeCompare(b.table_name));
      setAllTables(merged);
      setErrorMessage("");
    } catch (error) {
      console.error('Error fetching custom tables in TemplateEdit:', error);
      setErrorMessage(MESSAGES.ERRORS.FETCH_TABLES);
    }
  };

  const handleTableClick = async (table) => {
    setSelectedTable(table);
    setErrorMessage("");
    
    if (table._type === TABLE_TYPES.CUSTOM) {
      setSelectedTableData(table); // The table object itself
    } else {
      try {
        const data = await TemplateService.getTemplateTableData(table.table_name);
        setSelectedTableData(Array.isArray(data) ? data : []);
      } catch (error) {
        setErrorMessage(MESSAGES.ERRORS.FETCH_DATA);
        setSelectedTableData([]);
      }
    }
  };

  const refreshTableData = async () => {
    if (!selectedTable) return;
    
    try {
      const data = await TemplateService.getTemplateTableData(selectedTable.table_name);
      setSelectedTableData(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error refreshing table data:", error);
    }
  };

  const closeTable = () => {
    setSelectedTable(null);
    setSelectedTableData([]);
    setErrorMessage("");
  };

  return {
    // Data
    templateNames,
    customTables,
    allTables,
    selectedTable,
    selectedTableData,
    errorMessage,
    
    // Loading states
    isLoading,
    error,
    
    // Actions
    fetchCustomTables,
    handleTableClick,
    refreshTableData,
    closeTable,
    setSelectedTableData,
    setSelectedTable,
    setErrorMessage
  };
};
